import { Injectable, Injector } from '@angular/core';
import { ReportSection } from 'src/app/shared/components/diligence/report-sections';
import { PDFDocument } from '../interfaces/pdf-document.interface';
import { PdfTableService } from './pdf-table.service';
import { R8PdfRendererService } from './r8-pdf-renderer.service';
import { R8ReportModel } from '../models/r8-report.model';
import { AviReportResponse } from '../models/avi-report.model';
import { AviPdfRendererService } from './avi-pdf-renderer.service';

interface BlobStream {
  toBlobURL: (type: string) => string;
  on: (event: string, callback: (error?: Error) => void) => void;
}

declare global {
  interface Window {
    PDFKit: {
      PDFDocument: new (options?: { bufferPages?: boolean; size?: string; margins?: { top: number; bottom: number; left: number; right: number } }) => PDFDocument;
      blobStream: () => BlobStream;
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class PdfService {
  private readonly DISCLAIMER_ANCHOR = 'disclaimer_section';

  private loadImage(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  async generateR8ReportPdf(model: R8ReportModel, dueDiligence: any, renderer: R8PdfRendererService): Promise<void> {
    const coverImage = await this.loadImage('/assets/ucb-report-cover.png');
    return new Promise((resolve, reject) => {
      try {
        const doc = new window.PDFKit.PDFDocument({
          bufferPages: true,
          size: 'A4',
          margins: { top: 42, bottom: 42, left: 42, right: 42 }
        });
        const stream = doc.pipe(window.PDFKit.blobStream());
        renderer.drawDocument(doc, model, coverImage);
        doc.end();
        stream.on('finish', () => {
          try {
            const url = stream.toBlobURL('application/pdf');
            const link = document.createElement('a');
            link.href = url;
            link.download = `vessel-ucb-${model.vesselImo || 'report'}-${String(dueDiligence?.uuid || 'no-id').slice(-5)}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            resolve();
          } catch (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
          }
        });
        stream.on('error', (error: Error) => reject(error ?? new Error('PDF stream failed')));
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  async generateAviReportPdf(response: AviReportResponse, dueDiligence: any, renderer: AviPdfRendererService): Promise<void> {
    const coverImage = await this.loadImage('/assets/avi-cover-clean.png');
    return new Promise((resolve, reject) => {
      try {
        const doc = new window.PDFKit.PDFDocument({ bufferPages: true, size: 'A4', margins: { top: 0, bottom: 0, left: 0, right: 0 } });
        const stream = doc.pipe(window.PDFKit.blobStream());
        renderer.renderDocument(doc, response, coverImage);
        doc.end();
        stream.on('finish', () => {
          const url = stream.toBlobURL('application/pdf');
          const link = document.createElement('a');
          link.href = url;
          link.download = `avi-${response.avi.imo || 'report'}-${String(dueDiligence?.uuid || 'no-id').slice(-5)}.pdf`;
          document.body.appendChild(link); link.click(); document.body.removeChild(link); resolve();
        });
        stream.on('error', (error: Error) => reject(error));
      } catch (error) { reject(error instanceof Error ? error : new Error(String(error))); }
    });
  }

  async generateReportPdf(sections: ReportSection[], packageName: string, data: any, dueDiligence: any): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const logoDataUrl = await this.loadImage('../../../assets/TevunaLogo.svg');
        const doc = new window.PDFKit.PDFDocument({
          bufferPages: true,
          margins: { top: 50, bottom: 50, left: 50, right: 50 }
        });

        const stream = doc.pipe(window.PDFKit.blobStream());
        // Handle different data structures: R9 uses vesselData.vesselName, others use generalInfo.companyName
        const companyName = data.generalInfo?.companyName
          || data.vesselData?.vesselName
          || data.vesselData?.ship?.name
          || 'Report';

        this.addTitlePage(doc, packageName, companyName, logoDataUrl);

        sections.forEach(section => {
          if (section.print) {
            this.addSection(doc, section, data, dueDiligence);
          }
        });

        // Add page numbers to all pages
        const pages = doc.bufferedPageRange();
        for (let i = 0; i < pages.count; i++) {
          doc.switchToPage(i);

          // Save the old margin
          const oldBottomMargin = doc.page.margins.bottom;
          // Temporarily remove bottom margin
          doc.page.margins.bottom = 0;

          doc.fontSize(10)
             .text(
               `Page ${i + 1} of ${pages.count}`,
               doc.page.width - 150,
               doc.page.height - (oldBottomMargin / 2),
               { align: 'right' }
             );

          // Restore the margin
          doc.page.margins.bottom = oldBottomMargin;
        }

        doc.end();

        stream.on('finish', () => {
          try {
            const url = stream.toBlobURL('application/pdf');
            const link = document.createElement('a');
            const reportType = packageName.replace(/\s+/g, '-');
            const reportUuid = dueDiligence?.uuid || 'no-id';
            const last5Digits = reportUuid.slice(-5);
            const safeCompanyName = (companyName || 'report').toLowerCase().replace(/\s+/g, '-');
            const fileName = `${safeCompanyName}-${reportType.toLowerCase()}-${last5Digits}.pdf`;
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            resolve();
          } catch (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
          }
        });

        stream.on('error', (error: Error) => {
          reject(error);
        });
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  private addTitlePage(doc: PDFDocument, packageName: string, companyName: string, logoDataUrl: string): void {
    // Add logo - centered horizontally (page width is 612, so center is 306)
    doc.image(logoDataUrl, 156, 50, {
      fit: [300, 150],
      align: 'center',
      valign: 'center'
    });

    // Add text with dynamic positioning
    doc.fontSize(25)
       .font('Helvetica-Bold')
       .fillColor('#172554')
       .text(companyName, 100, 250, { align: 'center', width: 400 })
       .moveDown(2)
       .fontSize(25)
       .font('Helvetica')
       .text(`${packageName} Report`, 100, doc.y, { align: 'center', width: 400 })
       .moveDown(2)
       .fontSize(10)
       .fillColor('blue')
       .text('View report in browser', 100, doc.y, {
         align: 'center',
         width: 400,
         link: window.location.href,
         underline: true
       })
       .fillColor('#172554')
       .moveDown(4)
       .fontSize(12)
       .text(`Generated on ${new Date().toLocaleDateString()}`, 100, doc.y, { align: 'center', width: 400 })
       .moveDown(2)
       .fontSize(10)
       .text('By proceeding, you acknowledge and agree to be bound by the Disclaimer', 100, doc.y, {
         align: 'center',
         width: 400,
         goTo: this.DISCLAIMER_ANCHOR,
         underline: true
       });

    doc.addPage();
    doc.fillColor('black'); // Reset color for next pages
  }

  private addSection(doc: PDFDocument, section: ReportSection, data: any, dueDiligence: any): void {
    const ComponentClass = section.component;
    if (ComponentClass.renderToPdf) {
      // Pass section.data (which contains tab/spec) for spec-based reports
      // For old components, section.data contains the component-specific data
      const renderData = section.data || data;
      ComponentClass.renderToPdf(doc, renderData, dueDiligence);
    }
    doc.moveDown(10);
  }
}
